import { usePermissions } from '../hooks/usePermissions';

import UnauthorizedPage from './UnauthorizedPage';

import type { PermissionString } from '@/types/permissions';

/**
 * Gate children behind a permission. Pass a single `permission`, or `anyOf` a
 * list (the caller is allowed if they hold ANY of them — e.g. a resource scope
 * OR the `read:all` admin sentinel, mirroring the backend's requirePermission).
 * Renders UnauthorizedPage when the check fails.
 */
export default function RequirePermission({
  permission,
  anyOf,
  children,
}: {
  permission?: PermissionString;
  anyOf?: PermissionString[];
  children: React.ReactNode;
}) {
  const { hasPermission, hasAnyPermission, loading } = usePermissions();

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-moss-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const allowed = anyOf
    ? hasAnyPermission(...anyOf)
    : permission
      ? hasPermission(permission)
      : false;
  if (!allowed) {
    return <UnauthorizedPage />;
  }

  return <>{children}</>;
}
